import { X } from 'lucide-react'
import { useCallback, useState } from 'react'
import TypeAheadSearch from './ui/type-ahead-search'

/**
 * Multi-select for a prize's winners. Pick one person, or several when they
 * tied (ticket bdec6a1f).
 *
 * @param {Array<{id: string, full_name: string}>} winners - current selection
 * @param {Function} onChange - receives the new selection array
 * @param {Function} onSearch - async (query) => [{ user_id, full_name }]
 */
export default function PrizeWinnersPicker({ winners, onChange, onSearch, placeholder, disabled = false }) {
  // Remount the search box after each pick so it clears for the next name.
  const [searchKey, setSearchKey] = useState(0)

  const selectedIds = winners.map((w) => w.id).join(',')
  const searchUnselected = useCallback(async (query) => {
    const taken = new Set(selectedIds ? selectedIds.split(',') : [])
    const results = await onSearch(query)
    return (results || []).filter((u) => !taken.has(u.user_id))
  }, [onSearch, selectedIds])

  const add = (user) => {
    if (!user) return
    if (!winners.some((w) => w.id === user.user_id)) {
      onChange([...winners, { id: user.user_id, full_name: user.full_name }])
    }
    setSearchKey((k) => k + 1)
  }

  const remove = (id) => onChange(winners.filter((w) => w.id !== id))

  return (
    <div className="space-y-2">
      {winners.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Selected winners">
          {winners.map((w) => (
            <li
              key={w.id}
              className="flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-sm"
            >
              <span>{w.full_name || 'Unknown'}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(w.id)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${w.full_name || 'winner'}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <TypeAheadSearch
        key={searchKey}
        value=""
        onChange={add}
        onSearch={searchUnselected}
        getItemLabel={(u) => u.full_name || 'Unknown'}
        getItemValue={(u) => u.user_id}
        placeholder={placeholder || (winners.length ? 'Add another winner (tie)...' : 'Search for a winner...')}
        emptyMessage="No users found"
        disabled={disabled}
      />
      {winners.length > 1 && (
        <p className="text-xs text-muted-foreground">
          {winners.length} winners will share this prize as a tie.
        </p>
      )}
    </div>
  )
}
